import { errorMessage } from '@/util/errorUtil'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql/language'
import {
    createDisposer,
    type Disposable,
    logDebug,
    logError,
    logWarn,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { getSocketAuthToken } from '@/auth/authRef'
import { getDeviceInfo } from '@/device/device'
import { getDeviceConfig } from '@/device/deviceConfig'
import { FireflyApiKeys } from '@/service/gql'
import { ctx } from '@/util/context'

/**
 * Hermes pub/sub via the graphql-ws protocol. The endpoint runs over a
 * WebSocket and accepts subscriptions for any subscription operation
 * defined in the Firefly schema. See the Hermes guide for protocol
 * details: connection_init / subscribe / next / complete / error.
 *
 * Browsers can't set custom headers on the WebSocket upgrade; auth
 * flows through the connection_init payload instead. The server
 * validates the access token / api-key from there.
 */
const HERMES_ENDPOINT = 'wss://gateway.iad.prod.hermes.music.amazon.dev'

const { target } = ctx('hermes')

/**
 * graphql-ws message shapes — narrow but enough for what Hermes
 * sends/receives.
 */
type WsMessage =
    | { readonly type: 'connection_ack' }
    | { readonly type: 'ping' }
    | { readonly type: 'pong' }
    | { readonly type: 'next'; readonly id: string; readonly payload: { readonly data?: unknown } }
    | {
          readonly type: 'error'
          readonly id: string
          readonly payload: ReadonlyArray<{ readonly message: string }>
      }
    | { readonly type: 'complete'; readonly id: string }

/**
 * Lazily-shared connection. graphql-ws supports up to 100 concurrent
 * subscriptions per socket, so we hold a single connection and
 * multiplex by subscription id. Closes automatically once the last
 * subscription is disposed.
 */
class HermesConnection {
    private socket: WebSocket | null = null
    private connecting: Promise<WebSocket> | null = null
    private nextId = 0
    private readonly handlers = new Map<string, (msg: Extract<WsMessage, { id: string }>) => void>()

    /**
     * Reacts to a socket closing. Guarded by identity against a belated
     * `close` from a socket a newer connection attempt already superseded —
     * otherwise a stale close could clear the live connection's state and
     * spuriously error out its handlers.
     */
    private onSocketClose(ws: WebSocket, ev: CloseEvent): void {
        if (this.socket !== ws) return
        logWarn(target, 'connection closed', {
            code: ev.code,
            reason: ev.reason,
            wasClean: ev.wasClean,
        })
        this.socket = null
        this.connecting = null
        // Surface a synthetic error to every active subscription
        // so callers can react. The handlers detach themselves
        // on receipt.
        for (const [id, handler] of this.handlers) {
            handler({
                type: 'error',
                id,
                payload: [
                    {
                        message: `Hermes connection closed (${ev.code}${
                            ev.reason ? `: ${ev.reason}` : ''
                        })`,
                    },
                ],
            })
        }
        this.handlers.clear()
    }

    private async getSocket(): Promise<WebSocket> {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) return this.socket
        if (this.connecting) return this.connecting
        const attempt = (async () => {
            // Browsers can't set custom headers on the WebSocket upgrade, so
            // the auth Hermes expects (api key, device ids, bearer token) rides
            // the connection_init payload below; the gateway validates it there.
            const { deviceId } = getDeviceInfo()
            const { deviceTypeId } = getDeviceConfig()
            const token = await getSocketAuthToken()
            logDebug(target, `connecting to hermes: ${HERMES_ENDPOINT}`)
            const ws = new WebSocket(HERMES_ENDPOINT, 'graphql-transport-ws')
            this.socket = ws
            ws.addEventListener('message', (ev) => this.onMessage(ev))
            ws.addEventListener('close', (ev) => this.onSocketClose(ws, ev))
            ws.addEventListener('error', () => logError(target, 'websocket error'))
            await new Promise<void>((resolve, reject) => {
                ws.addEventListener('open', () => resolve(), { once: true })
                ws.addEventListener('error', () => reject(new Error('hermes ws error')), {
                    once: true,
                })
            })
            // graphql-ws requires connection_init before subscribe is
            // allowed.
            ws.send(
                JSON.stringify({
                    type: 'connection_init',
                    payload: {
                        'x-api-key': FireflyApiKeys.HERMES_API_KEY,
                        'x-device-id': deviceId,
                        'x-device-type': deviceTypeId,
                        ...(token ? { authorization: `Bearer ${token}` } : {}),
                    },
                })
            )
            await new Promise<void>((resolve, reject) => {
                const onAckMsg = (ev: MessageEvent<string>) => {
                    let parsed: WsMessage
                    try {
                        parsed = JSON.parse(ev.data) as WsMessage
                    } catch {
                        return
                    }
                    if (parsed.type === 'connection_ack') {
                        ws.removeEventListener('message', onAckMsg)
                        resolve()
                    }
                }
                ws.addEventListener('message', onAckMsg)
                ws.addEventListener('error', () => reject(new Error('hermes ws error')), {
                    once: true,
                })
            })
            return ws
        })()
        this.connecting = attempt
        try {
            return await attempt
        } catch (e) {
            // A failed attempt must not leave `connecting` cached — a socket
            // that errors before `open` still assigns `this.socket` above, so
            // gating this on `this.socket` (as before) never actually clears
            // it, and every subsequent subscribe replays the same stale
            // rejection forever instead of retrying.
            if (this.connecting === attempt) this.connecting = null
            throw e
        }
    }

    private onMessage(ev: MessageEvent<string>): void {
        logDebug(target, 'onMessage', ev)
        let msg: WsMessage
        try {
            msg = JSON.parse(ev.data) as WsMessage
        } catch {
            logWarn(target, 'malformed message')
            return
        }
        switch (msg.type) {
            case 'ping':
                this.socket?.send(JSON.stringify({ type: 'pong' }))
                return
            case 'next':
            case 'error':
            case 'complete': {
                const handler = this.handlers.get(msg.id)
                if (handler) handler(msg)
                return
            }
            default:
                return
        }
    }

    /**
     * Begin a subscription. Returns an unsubscribe function — call it
     * to send `complete` to the gateway and detach the handler. When
     * the last subscription unsubscribes the underlying WebSocket
     * stays open (lazy reuse) until the page unloads or the gateway
     * closes it.
     */
    subscribe(
        operationName: string,
        query: string,
        variables: Record<string, unknown>,
        onNext: (data: unknown) => void,
        onError?: (message: string) => void
    ): Unsubscribe {
        const id = String(++this.nextId)
        let active = true
        const handler = (msg: Extract<WsMessage, { id: string }>) => {
            logDebug(target, 'subscribe handler', id)
            if (msg.type === 'next') {
                if (msg.payload.data !== undefined) onNext(msg.payload.data)
            } else if (msg.type === 'error') {
                const message = msg.payload[0]?.message ?? 'unknown'
                logWarn(target, 'subscription error', id, message)
                onError?.(message)
            } else {
                // 'complete' — the gateway has finished sending events
                // for this id. Drop the handler.
                this.handlers.delete(id)
            }
        }
        this.handlers.set(id, handler)
        // Fire the subscribe message once the connection is ready.
        // If the connection drops before we get there, the handler
        // gets a synthetic error from `close` above.
        this.getSocket()
            .then((ws) => {
                if (!active) return
                logDebug(target, 'subscribe', id, operationName)
                // Mirror DMSF_Service / DMTVAndroid: query + operationName +
                // variables. The gateway resolves the operation server-side
                // and refuses inline-only or eventType-only subscribe
                // payloads with "Subscription not allowed: graphql:unknown".
                ws.send(
                    JSON.stringify({
                        id,
                        type: 'subscribe',
                        payload: { operationName, query, variables },
                    })
                )
            })
            .catch((e: unknown) => {
                if (!active) return
                this.handlers.delete(id)
                onError?.(errorMessage(e))
            })
        return () => {
            active = false
            this.handlers.delete(id)
            // Best-effort: tell the gateway we're done. If the socket
            // is gone (closed during the subscription) there's
            // nothing to send; the server has already stopped sending.
            const ws = this.socket
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ id, type: 'complete' }))
            }
        }
    }
}

let connection: HermesConnection | null = null

function getConnection(): HermesConnection {
    if (!connection) connection = new HermesConnection()
    return connection
}

/**
 * Opens a Hermes subscription for `options.query` with the given
 * variables. Returns a Disposable — calling `dispose()` sends
 * `complete` to the gateway and detaches the listener.
 *
 * `onNext` receives the `payload.data` of each `next` message
 * directly; the caller types it via the TypedDocumentNode generic.
 * `onError` (optional) is called when the gateway rejects the
 * subscription or the underlying connection drops.
 */
export function subscribeToHermes<TData, TVariables>(options: {
    readonly operationName: string
    readonly query: TypedDocumentNode<TData, TVariables>
    readonly variables: TVariables
    readonly onNext: (data: TData) => void
    readonly onError?: (message: string) => void
}): Disposable {
    const disposer = createDisposer()
    const queryString = print(options.query)
    const hermesSub = getConnection().subscribe(
        options.operationName,
        queryString,
        options.variables as Record<string, unknown>,
        (data) => options.onNext(data as TData),
        options.onError
    )
    disposer.add(hermesSub)
    return disposer
}
